import { Box, Flex, ScrollArea, Skeleton, Text } from '@mantine/core';
import { i18nKeys, useTranslation } from 'locales';
import { isDefined, isEmpty, range } from 'remeda';
import { useLoadPaidByReminder } from 'shared/hooks/use-load-paid-by-reminder';

import { AnalyticsCard } from '../AnalyticsCard';
import { PaidByReminderStageLegend } from '../charts/PaidByReminderStageLegend';
import { PaidByReminderWorkflows } from '../charts/PaidByReminderWorkflows';
import { toStageLegendSegments } from '../charts/paid-by-reminder-segments';

const PLACEHOLDER_ROWS = 3;

const LIST_RESET = { padding: 0, listStyleType: 'none' } as const;

// The plan list must not drive the card's height, or the row grows to fit however many
// plans a tenant has and the sibling card stretches with it. flex-basis 0 keeps it out
// of the intrinsic height; the card fills the row the taller sibling sets.
const FILL_REMAINING = { flex: '1 1 0', minHeight: 0 } as const;

const CARD_BODY = { display: 'flex', flexDirection: 'column', ...FILL_REMAINING } as const;

const PaidByReminderWorkflowsPlaceholder = () => (
  <Flex component="ul" aria-busy direction="column" gap="md" style={LIST_RESET}>
    {range(0, PLACEHOLDER_ROWS).map(row => (
      <Box key={row} component="li">
        <Skeleton h={14} w={160} radius={4} mb="xs" />
        <Skeleton h={30} radius={8} />
      </Box>
    ))}
  </Flex>
);

type PaidByReminderWorkflowsCardProps = {
  periodMonths: number;
};

export const PaidByReminderWorkflowsCard = ({ periodMonths }: PaidByReminderWorkflowsCardProps) => {
  const { t } = useTranslation();
  const { paidByReminder, isPaidByReminderLoading } = useLoadPaidByReminder({ periodMonths });

  return (
    <AnalyticsCard
      title={t(i18nKeys.ANALYTICS.PAID_BY_REMINDER.WORKFLOWS_TITLE)}
      panelProps={{ fullHeight: true }}
    >
      {isPaidByReminderLoading && <PaidByReminderWorkflowsPlaceholder />}
      {isDefined(paidByReminder) &&
        (isEmpty(paidByReminder.workflows) ? (
          <Text mt="md" size="xs" c="gray.6">
            {t(i18nKeys.ANALYTICS.PAID_BY_REMINDER.NO_WORKFLOWS)}
          </Text>
        ) : (
          <Box style={CARD_BODY}>
            <ScrollArea type="auto" offsetScrollbars style={FILL_REMAINING}>
              <PaidByReminderWorkflows workflows={paidByReminder.workflows} />
            </ScrollArea>
            <Box mt="md">
              <PaidByReminderStageLegend
                segments={toStageLegendSegments(paidByReminder.workflows, t)}
              />
            </Box>
          </Box>
        ))}
    </AnalyticsCard>
  );
};
